import { ReactNode, useEffect, useRef, useState } from 'react';

interface DeferredSectionProps {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
}

const defaultFallback = <div aria-hidden="true" className="min-h-[24rem]" />;

/**
 * Defers mounting a below-the-fold section until it approaches the viewport.
 * The wrapper keeps a stable DOM anchor while preserving the child's own UI.
 */
export function DeferredSection({
  children,
  fallback = defaultFallback,
  rootMargin = '600px',
}: DeferredSectionProps) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setShouldRender(true);
      return;
    }

    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          setShouldRender(true);
        }
      },
      { rootMargin, threshold: 0 },
    );

    observer.observe(anchor);
    return () => observer.disconnect();
  }, [rootMargin, shouldRender]);

  return (
    <div ref={anchorRef} data-deferred-section data-deferred-state={shouldRender ? 'mounted' : 'pending'}>
      {shouldRender ? children : fallback}
    </div>
  );
}
